// ============================================================================
// CLEO'S SECRET — ClinicalCopilot
// File: src/pages/ClinicalCopilot.jsx
//
// Robyn's AI-assisted clinical workspace. Opens from a patient's row in
// ClinicalDashboard via onNavigate('copilot', { patientId }). The GP fills
// in consultation details, Cleo generates four document types (consultation
// note, patient letter, referral letter, investigation request), the GP
// edits and copies each to send.
//
// Workstation pattern: obsidian header chrome, cream main canvas, two-pane
// workspace that starts single-column and expands when documents generate.
//
// API calls preserved:
//   • /api/get-patient-detail   — load patient + intake summary
//   • /api/create-consultation  — save draft (also returns consultationId)
//   • /api/generate-documents   — AI document generation
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, FS, P,
  Spinner,
} from '../lib/design-tokens';
import RequireAdmin from '../components/RequireAdmin';

// ───────── Helpers ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function docTypeLabel(t) {
  var labels = {
    consultation_note: 'Consultation note',
    patient_letter: 'Patient letter',
    referral_letter: 'Referral letter',
    investigation_request: 'Investigation request',
  };
  return labels[t] || t;
}

function patientName(patient) {
  if (!patient) return 'Unknown patient';
  if (patient.first_name && patient.last_name) {
    return patient.first_name + ' ' + patient.last_name;
  }
  return patient.first_name || patient.email || 'Unknown patient';
}

// ───────── SectionLabel ─────────
function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: FF,
      fontSize: 'clamp(11px, 0.95vw, 13px)',
      fontWeight: 700,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color: P.BR2,
      marginBottom: 'clamp(14px, 1.6vw, 18px)',
      paddingBottom: 'clamp(10px, 1.2vw, 14px)',
      borderBottom: '1px solid ' + P.SAND3,
    }}>{children}</div>
  );
}

// ───────── Field — labeled textarea for the consultation form ─────────
function Field({ label, optional, value, onChange, placeholder, rows }) {
  return (
    <div style={{ marginBottom: 'clamp(16px, 1.8vw, 22px)' }}>
      <label style={{
        display: 'block',
        fontFamily: FF,
        fontSize: 'clamp(10px, 0.85vw, 11px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: P.BR2,
        marginBottom: 8,
      }}>
        {label}
        {optional && (
          <span style={{
            fontFamily: FF,
            fontStyle: 'italic',
            fontWeight: 400,
            textTransform: 'none',
            letterSpacing: '0.02em',
            color: P.TF,
            fontSize: '0.95em',
            marginLeft: 6,
          }}>(optional)</span>
        )}
      </label>
      <textarea
        value={value}
        onChange={function(e) { onChange(e.target.value); }}
        placeholder={placeholder}
        rows={rows || 3}
        style={{
          width: '100%',
          padding: '14px 16px',
          fontFamily: FF,
          fontSize: 'clamp(14px, 1.2vw, 15px)',
          color: P.INK,
          background: P.PAP,
          border: '1px solid ' + P.SAND3,
          outline: 'none',
          resize: 'vertical',
          boxSizing: 'border-box',
          lineHeight: 1.7,
          transition: 'border-color 0.2s',
        }}
        onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
        onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ClinicalCopilot — outer wrapper. Gates on patients.is_admin via
// RequireAdmin so the inner page (and its data-fetching useEffects) only
// mount when the caller is confirmed admin. See src/components/RequireAdmin.jsx.
// ═════════════════════════════════════════════════════════════════════════
export default function ClinicalCopilot(props) {
  return (
    <RequireAdmin email={props.email} isAdmin={props.isAdmin} onNavigate={props.onNavigate}>
      <ClinicalCopilotInner
        onNavigate={props.onNavigate}
        patientId={props.patientId}
      />
    </RequireAdmin>
  );
}

function ClinicalCopilotInner(props) {
  var onNavigate = props.onNavigate;
  var patientId = props.patientId;

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var patientState = useState(null);
  var patient = patientState[0];
  var setPatient = patientState[1];

  var intakeState = useState(null);
  var intake = intakeState[0];
  var setIntake = intakeState[1];

  var consultationIdState = useState(null);
  var consultationId = consultationIdState[0];
  var setConsultationId = consultationIdState[1];

  // GP input fields
  var presentingState = useState('');
  var presenting = presentingState[0];
  var setPresenting = presentingState[1];

  var findingsState = useState('');
  var findings = findingsState[0];
  var setFindings = findingsState[1];

  var impressionState = useState('');
  var impression = impressionState[0];
  var setImpression = impressionState[1];

  var planState = useState('');
  var plan = planState[0];
  var setPlan = planState[1];

  var followUpState = useState('');
  var followUp = followUpState[0];
  var setFollowUp = followUpState[1];

  var referralToState = useState('');
  var referralTo = referralToState[0];
  var setReferralTo = referralToState[1];

  var referralReasonState = useState('');
  var referralReason = referralReasonState[0];
  var setReferralReason = referralReasonState[1];

  var investigationsState = useState('');
  var investigations = investigationsState[0];
  var setInvestigations = investigationsState[1];

  // Generation + save state
  var generatingState = useState(false);
  var generating = generatingState[0];
  var setGenerating = generatingState[1];

  var documentsState = useState([]);
  var documents = documentsState[0];
  var setDocuments = documentsState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var savedState = useState(false);
  var saved = savedState[0];
  var setSaved = savedState[1];

  var copiedDocState = useState(null);
  var copiedDoc = copiedDocState[0];
  var setCopiedDoc = copiedDocState[1];

  // ───────── Load patient + intake ─────────
  useEffect(function() {
    if (!patientId) { setLoading(false); return; }

    fetch('/api/get-patient-detail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patientId: patientId }),
    })
      .then(function(res) { return res.json(); })
      .then(function(result) {
        setPatient(result.patient);
        var intakes = result.intakes || [];
        var completed = intakes.find(function(i) { return i.status === 'completed'; });
        if (completed && completed.clinical_summary) {
          setIntake(completed.clinical_summary);
          if (completed.clinical_summary.primary_concern) {
            setPresenting(completed.clinical_summary.primary_concern);
          }
        }
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }, [patientId]);

  // ───────── Save consultation draft ─────────
  function saveConsultation() {
    if (!patient) return Promise.reject(new Error('No patient'));

    setSaving(true);
    return fetch('/api/create-consultation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        consultationId: consultationId,
        patientId: patient.id,
        practitionerName: "Dr Robyn O'Sullivan",
        consultationDate: new Date().toISOString().split('T')[0],
        presentingComplaint: presenting || null,
        findings: findings || null,
        impression: impression || null,
        plan: plan || null,
        followUp: followUp || null,
        referralTo: referralTo || null,
        referralReason: referralReason || null,
        investigationsRequested: investigations || null,
      }),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Save failed'); });
        return res.json();
      })
      .then(function(data) {
        setConsultationId(data.consultationId);
        setSaved(true);
        setSaving(false);
        setTimeout(function() { setSaved(false); }, 2000);
        return data.consultationId;
      })
      .catch(function(err) {
        setSaving(false);
        alert(err.message);
        throw err;
      });
  }

  // ───────── Generate documents (chains after save) ─────────
  function generateDocuments() {
    setGenerating(true);

    saveConsultation()
      .then(function(id) {
        return fetch('/api/generate-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ consultationId: id }),
        });
      })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Generation failed'); });
        return res.json();
      })
      .then(function(data) {
        setDocuments(data.documents || []);
        setGenerating(false);
      })
      .catch(function(err) {
        setGenerating(false);
        alert('Could not generate documents: ' + err.message);
      });
  }

  function copyToClipboard(docId, content) {
    var textarea = document.createElement('textarea');
    textarea.value = content;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      setCopiedDoc(docId);
      setTimeout(function() { setCopiedDoc(null); }, 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
    document.body.removeChild(textarea);
  }

  function updateDocumentContent(docId, newContent) {
    setDocuments(documents.map(function(d) {
      return d.id === docId ? Object.assign({}, d, { content: newContent }) : d;
    }));
  }

  // ────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        background: P.OBS,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO2} size={48} />
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // PATIENT-NOT-FOUND STATE
  // ────────────────────────────────────────────────────────────────────────
  if (!patient) {
    return (
      <div style={{
        background: P.OBS,
        color: P.IVY,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(24px, 4vw, 60px)',
        textAlign: 'center',
        fontFamily: FF,
      }}>
        <div style={{ maxWidth: 480 }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            color: P.GO2,
            marginBottom: 18,
          }}>Patient not found</div>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(20px, 2.2vw, 28px)',
            fontStyle: 'italic',
            color: P.GO2,
            lineHeight: 1.5,
            marginBottom: 'clamp(28px, 3vw, 40px)',
          }}>This record could not be loaded.</p>
          <button
            onClick={function() { if (onNavigate) onNavigate('clinical'); }}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 32px',
              background: P.GO,
              color: P.OBS,
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = P.GO2; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = P.GO; }}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  var canGenerate = !!findings && !!plan;
  var hasDocuments = documents.length > 0;

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>

      {/* ═════════ Obsidian header ═════════ */}
      <header style={{
        background: P.OBS,
        borderBottom: '1px solid ' + P.GO,
        padding: 'clamp(20px, 2.4vw, 32px) clamp(24px, 3vw, 40px)',
      }}>
        <div style={{
          maxWidth: 1500,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.36em',
              textTransform: 'uppercase',
              color: P.GO2,
              marginBottom: 6,
            }}>Cleo's Secret &middot; Workstation</div>
            <h1 style={{
              fontFamily: FF,
              fontSize: 'clamp(22px, 2.4vw, 32px)',
              fontWeight: 400,
              color: P.PAP,
              lineHeight: 1.15,
              letterSpacing: '-0.008em',
              margin: 0,
            }}>Clinical <span style={{ fontStyle: 'italic', color: P.GO2 }}>copilot.</span></h1>
          </div>

          <button
            onClick={function() { if (onNavigate) onNavigate('clinical'); }}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.9vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              padding: '12px 22px',
              background: 'transparent',
              color: P.GO2,
              border: '1px solid ' + P.GO2,
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(224, 198, 133, 0.1)'; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
            Dashboard
          </button>
        </div>
      </header>

      {/* ═════════ Main canvas ═════════ */}
      <main style={{
        maxWidth: 1500,
        margin: '0 auto',
        padding: 'clamp(28px, 3.4vw, 48px) clamp(24px, 3vw, 40px) clamp(48px, 6vw, 80px)',
      }}>

        {/* ─── Patient header card ─── */}
        <div style={{
          padding: 'clamp(24px, 2.8vw, 36px)',
          background: P.PAP,
          border: '1px solid ' + P.SAND3,
          borderLeft: '3px solid ' + P.GO,
          marginBottom: 'clamp(28px, 3vw, 40px)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 24,
            flexWrap: 'wrap',
            marginBottom: intake ? 'clamp(20px, 2.2vw, 26px)' : 0,
          }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(10px, 0.85vw, 12px)',
                fontWeight: 700,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 8,
              }}>Consultation</div>
              <h2 style={{
                fontFamily: FF,
                fontSize: 'clamp(24px, 2.4vw, 32px)',
                fontWeight: 400,
                color: P.INK,
                lineHeight: 1.15,
                letterSpacing: '-0.012em',
                margin: '0 0 6px',
              }}>{patientName(patient)}</h2>
              <div style={{
                fontFamily: FS,
                fontSize: 'clamp(12px, 1vw, 14px)',
                color: P.TF,
                letterSpacing: '0.02em',
                wordBreak: 'break-word',
              }}>
                {patient.email}
                {patient.phone ? ' \u00b7 ' + patient.phone : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(10px, 0.85vw, 12px)',
                fontWeight: 700,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 8,
              }}>Today</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(16px, 1.4vw, 18px)',
                fontStyle: 'italic',
                color: P.BR2,
                lineHeight: 1.3,
              }}>{formatDate(new Date())}</div>
            </div>
          </div>

          {/* From-intake quote */}
          {intake && intake.primary_concern && (
            <div style={{
              padding: 'clamp(18px, 2vw, 24px) clamp(20px, 2.2vw, 26px)',
              background: 'rgba(201, 169, 97, 0.06)',
              borderLeft: '3px solid ' + P.GO,
            }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(10px, 0.85vw, 11px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 10,
              }}>From intake</div>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 16px)',
                fontStyle: 'italic',
                color: P.TX,
                lineHeight: 1.65,
                margin: 0,
              }}>{intake.primary_concern}</p>
            </div>
          )}
        </div>

        {/* ─── Two-pane workspace ─── */}
        <div className="cs-copilot-grid" style={{
          display: 'grid',
          gridTemplateColumns: hasDocuments ? '1fr 1fr' : '1fr',
          gap: 'clamp(24px, 2.6vw, 36px)',
          alignItems: 'start',
        }}>

          {/* ────── LEFT PANE — Consultation form ────── */}
          <div>
            <SectionLabel>Consultation details</SectionLabel>

            <Field
              label="Presenting complaint"
              value={presenting}
              onChange={setPresenting}
              placeholder="What the patient came in for."
              rows={2} />

            <Field
              label="Findings"
              value={findings}
              onChange={setFindings}
              placeholder="Examination findings, history, what was discussed."
              rows={4} />

            <Field
              label="Clinical impression"
              value={impression}
              onChange={setImpression}
              placeholder="Your clinical impression or working diagnosis."
              rows={3} />

            <Field
              label="Plan"
              value={plan}
              onChange={setPlan}
              placeholder="Treatment plan, medications, advice."
              rows={4} />

            <Field
              label="Follow-up"
              value={followUp}
              onChange={setFollowUp}
              placeholder="When to follow up, what to monitor."
              rows={2} />

            {/* Referral subsection */}
            <div style={{
              marginTop: 'clamp(28px, 3vw, 40px)',
              padding: 'clamp(22px, 2.4vw, 28px)',
              background: 'rgba(232,221,196,0.4)',
              border: '1px solid ' + P.SAND3,
            }}>
              <SectionLabel>Referral &middot; <span style={{
                fontFamily: FF,
                fontStyle: 'italic',
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: '0.02em',
                color: P.TF,
                fontSize: '0.85em',
              }}>optional</span></SectionLabel>
              <Field
                label="Refer to"
                value={referralTo}
                onChange={setReferralTo}
                placeholder="e.g. Dr Jane Smith, endocrinologist"
                rows={1} />
              <Field
                label="Reason for referral"
                value={referralReason}
                onChange={setReferralReason}
                placeholder="Clinical reason for the referral."
                rows={2} />
            </div>

            {/* Investigations subsection */}
            <div style={{
              marginTop: 'clamp(20px, 2.4vw, 28px)',
              padding: 'clamp(22px, 2.4vw, 28px)',
              background: 'rgba(232,221,196,0.4)',
              border: '1px solid ' + P.SAND3,
            }}>
              <SectionLabel>Investigations &middot; <span style={{
                fontFamily: FF,
                fontStyle: 'italic',
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: '0.02em',
                color: P.TF,
                fontSize: '0.85em',
              }}>optional</span></SectionLabel>
              <Field
                label="Investigations requested"
                value={investigations}
                onChange={setInvestigations}
                placeholder="e.g. FBE, TFTs, fasting glucose, vitamin D, ferritin."
                rows={3} />
            </div>

            {/* Action buttons */}
            <div style={{
              marginTop: 'clamp(28px, 3vw, 40px)',
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
            }}>
              <button
                onClick={saveConsultation}
                disabled={saving}
                style={{
                  flex: 1,
                  minWidth: 160,
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  padding: '14px 24px',
                  background: saved ? P.GREEN_BG : 'transparent',
                  color: saved ? P.GREEN : P.BR,
                  border: '1px solid ' + (saved ? P.GREEN : P.GO),
                  cursor: saving ? 'default' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={function(e) {
                  if (!saving && !saved) e.currentTarget.style.background = 'rgba(201, 169, 97, 0.08)';
                }}
                onMouseLeave={function(e) {
                  if (!saving && !saved) e.currentTarget.style.background = 'transparent';
                }}>
                {saving ? 'Saving\u2026' : saved ? 'Saved' : 'Save draft'}
              </button>
              <button
                onClick={generateDocuments}
                disabled={generating || !canGenerate}
                style={{
                  flex: 2,
                  minWidth: 240,
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  padding: '14px 24px',
                  background: canGenerate && !generating ? P.GO : 'rgba(201, 169, 97, 0.4)',
                  color: P.OBS,
                  border: 'none',
                  cursor: (generating || !canGenerate) ? 'default' : 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={function(e) {
                  if (canGenerate && !generating) e.currentTarget.style.background = P.GO2;
                }}
                onMouseLeave={function(e) {
                  if (canGenerate && !generating) e.currentTarget.style.background = P.GO;
                }}>
                {generating ? 'Generating\u2026' : 'Generate documents'}
              </button>
            </div>

            {!canGenerate && (
              <p style={{
                marginTop: 12,
                fontFamily: FF,
                fontSize: 'clamp(13px, 1.1vw, 14px)',
                fontStyle: 'italic',
                color: P.TF,
                textAlign: 'center',
                lineHeight: 1.5,
              }}>Findings and Plan are required before generating documents.</p>
            )}
          </div>

          {/* ────── RIGHT PANE — Generated documents (only when present) ────── */}
          {hasDocuments && (
            <div className="cs-copilot-docs">
              <SectionLabel>Generated documents</SectionLabel>

              {documents.map(function(doc) {
                var isCopied = copiedDoc === doc.id;
                return (
                  <div key={doc.id} style={{
                    marginBottom: 'clamp(20px, 2.2vw, 28px)',
                    background: P.PAP,
                    border: '1px solid ' + P.SAND3,
                  }}>
                    {/* Doc header */}
                    <div style={{
                      padding: 'clamp(14px, 1.6vw, 18px) clamp(18px, 2vw, 22px)',
                      background: 'rgba(201, 169, 97, 0.06)',
                      borderBottom: '1px solid ' + P.SAND3,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 12,
                      flexWrap: 'wrap',
                    }}>
                      <h3 style={{
                        fontFamily: FF,
                        fontSize: 'clamp(11px, 0.95vw, 13px)',
                        fontWeight: 700,
                        letterSpacing: '0.32em',
                        textTransform: 'uppercase',
                        color: P.BR2,
                        margin: 0,
                      }}>{docTypeLabel(doc.document_type)}</h3>
                      <button
                        onClick={function() { copyToClipboard(doc.id, doc.content); }}
                        style={{
                          fontFamily: FS,
                          fontSize: 'clamp(10px, 0.85vw, 11px)',
                          fontWeight: 600,
                          letterSpacing: '0.28em',
                          textTransform: 'uppercase',
                          padding: '8px 18px',
                          background: isCopied ? P.GREEN : P.INK,
                          color: P.PAP,
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                        }}
                        onMouseEnter={function(e) {
                          if (!isCopied) e.currentTarget.style.background = P.BR;
                        }}
                        onMouseLeave={function(e) {
                          if (!isCopied) e.currentTarget.style.background = P.INK;
                        }}>
                        {isCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>

                    {/* Doc editor */}
                    <textarea
                      value={doc.content}
                      onChange={function(e) { updateDocumentContent(doc.id, e.target.value); }}
                      style={{
                        width: '100%',
                        padding: 'clamp(18px, 2vw, 24px)',
                        fontFamily: FF,
                        fontSize: 'clamp(14px, 1.2vw, 15px)',
                        color: P.TX,
                        background: P.PAP,
                        border: 'none',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: 280,
                        boxSizing: 'border-box',
                        lineHeight: 1.85,
                        whiteSpace: 'pre-wrap',
                      }}
                    />
                  </div>
                );
              })}

              {/* Workflow note */}
              <div style={{
                padding: 'clamp(18px, 2vw, 24px) clamp(20px, 2.2vw, 28px)',
                background: 'rgba(201, 169, 97, 0.06)',
                borderLeft: '3px solid ' + P.GO,
              }}>
                <div style={{
                  fontFamily: FF,
                  fontSize: 'clamp(10px, 0.85vw, 11px)',
                  fontWeight: 700,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: P.BR2,
                  marginBottom: 10,
                }}>Next</div>
                <p style={{
                  fontFamily: FF,
                  fontSize: 'clamp(14px, 1.2vw, 15px)',
                  color: P.TL,
                  lineHeight: 1.7,
                  margin: 0,
                }}>Edit any document as needed, then click Copy to use it. Each document is saved to the patient's record and can be finalised later.</p>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* Responsive: stack panes below 1100px (this page needs a wider breakpoint
          than the other workstations because both panes contain dense forms) */}
      <style>{`
        @media (max-width: 1100px) {
          .cs-copilot-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
